import React, { FC } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { Rating } from "@components/ui/Rating";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { Image } from "@components/ui/Image";

export type ISupplierCard = {
  displayName: string;
  biography: string;
  url: string;
  picture: {
    url: string;
  };
  country: {
    name: string;
  };
  city: {
    name: string;
  };
  stats: {
    reviewsCount: number;
    rating: number;
  };
};

const SupplierCard: FC<ISupplierCard> = (payload) => {
  const router = useRouter();
  const { query } = router;

  let supplierUrl = payload.url;

  // concat filters to supplier link
  if (
    (query.t === undefined || query.t === "guides") &&
    (query.date !== "" || query.people !== "")
  ) {
    const queryParams = {
      ...(query.date !== undefined && { date: query.date }),
      ...(query.adults !== undefined && { adults: query.adults }),
      ...(query.children !== undefined && { children: query.children }),
      ...(query.infants !== undefined && { infants: query.infants }),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryString = new URLSearchParams(queryParams as any).toString();
    supplierUrl = queryString ? `${supplierUrl}?${queryString}` : supplierUrl;
  }

  return (
    <Link href={getAbsoluteUrl(supplierUrl)} target="_blank">
      <div className="flex flex-col bg-white border border-gray-100 overflow-hidden rounded-md shadow-lg w-full h-full mb-4 sm:mb-0">
        {/* Image with aspect ratio */}
        <div className="relative bg-gray-100">
          <div
            className="relative w-full"
            style={{ paddingBottom: "75%" /* 4:3 aspect */ }}
          >
            <Image
              src={payload.picture.url}
              alt={payload.displayName}
              className="w-full object-cover h-48"
              fill={true}
              activeCache={false}
            />
          </div>

          {/* Country badge wrapper */}
          <div className="absolute top-2 left-2 w-full pr-2">
            <div
              className="inline-block bg-gray-200 text-gray-600 rounded px-3 py-1 text-xs truncate"
              style={{ maxWidth: "40%" }}
            >
              {payload.country.name}
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full h-full px-3 py-1 md:py-3 justify-center lg:justify-start">
          {/* Mobile layout: Name and rating side-by-side, and in PC its vertical */}
          <div className="flex flex-row flex-wrap lg:flex-col h-auto items-center md:mb-auto">
            {/* User Title (50% on mobile, full on desktop) */}
            <div className="w-1/2 sm:w-full pr-2">
              <h2
                className="text-xl font-medium truncate overflow-hidden"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  lineHeight: "1.3rem",
                  textAlign: "start",
                }}
              >
                {payload.displayName}
              </h2>
            </div>

            {/* Rating (50% on mobile, full on desktop) */}
            {payload.stats.reviewsCount > 0 && (
              <div className="flex justify-end sm:justify-start w-1/2 lg:w-full text-xs">
                {/* Mobile view: xs */}
                <div className="block lg:hidden">
                  <Rating
                    rate={payload.stats.rating}
                    reviews={payload.stats.reviewsCount}
                    alwaysShowDecimals={true}
                    variant="stars"
                    size="xs"
                    className="mt-0 sm:mt-2"
                    starsAndReviewsTwoRow
                  />
                </div>

                {/* Desktop view: sm */}
                <div className="hidden lg:block">
                  <Rating
                    rate={payload.stats.rating}
                    reviews={payload.stats.reviewsCount}
                    alwaysShowDecimals={true}
                    variant="stars"
                    size="sm"
                    className="mt-0 sm:mt-4"
                    starsAndReviewsTwoRow
                  />
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="hidden lg:block mt-auto">
            <h3
              className="text-sm text-gray-500 leading-relaxed overflow-hidden mt-4"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: payload.stats.reviewsCount > 0 ? 3 : 5,
                lineHeight: "1.25rem",
                textAlign: "start",
              }}
            >
              {payload.biography}
            </h3>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SupplierCard;
